/**
 * Owner: Masa Bayu
 * File: advanced-offline.ts
 * Date:Time: 2026-09-16 18:50
 * Description: T8-E2 OfflinePackage policy — entitlement-gated; ≠ grant store.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertT8E2SecurityIntact } from './advanced-security';
import { isEntitlementActiveForRead } from './entitlement-boundary';
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
import { QXREAD_OFFLINE_PACKAGE_STATES } from './pwa-foundation';
function assertPackageState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_OFFLINE_PACKAGE_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_OFFLINE_PACKAGE_STATES.join(', ')}`);
    }
    return value;
}
export function prepareOfflinePackage(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead OfflinePackage prepare');
    assertT8E2SecurityIntact();
    assertValidId(input.accountId, 'QXRead OfflinePackage accountId');
    assertValidId(input.publicationId, 'QXRead OfflinePackage publicationId');
    if (input.entitlement.accountId !== input.accountId) {
        return Object.freeze({ allowed: false, reason: 'Entitlement account mismatch' });
    }
    if (input.entitlement.publicationId !== input.publicationId) {
        return Object.freeze({ allowed: false, reason: 'Entitlement publication mismatch' });
    }
    if (!isEntitlementActiveForRead(input.entitlement)) {
        return Object.freeze({
            allowed: false,
            reason: 'Active entitlement required for OfflinePackage prepare',
        });
    }
    const preparedAt = input.preparedAt ?? new Date().toISOString();
    assertTimestamp(preparedAt, 'QXRead OfflinePackage preparedAt');
    const offlinePackageId = input.offlinePackageId !== undefined ? input.offlinePackageId : createQxreadId('qxrdpkg');
    assertValidId(offlinePackageId, 'QXRead offlinePackageId');
    return Object.freeze({
        allowed: true,
        offlinePackage: Object.freeze({
            offlinePackageId,
            accountId: input.accountId,
            publicationId: input.publicationId,
            entitlementId: input.entitlement.entitlementId,
            state: assertPackageState('ready', 'QXRead OfflinePackage state'),
            updatedAt: preparedAt,
            grantsEntitlement: false,
            mutatesQxp: false,
            executesFlip: false,
        }),
    });
}
export function purgeOfflinePackageOnRevoke(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead OfflinePackage purge');
    assertT8E2SecurityIntact();
    const purgedAt = input.purgedAt ?? new Date().toISOString();
    assertTimestamp(purgedAt, 'QXRead OfflinePackage purgedAt');
    const shouldPurge = input.entitlement.state === 'revoked' ||
        input.entitlement.state === 'expired' ||
        !isEntitlementActiveForRead(input.entitlement);
    return Object.freeze({
        ...input.offlinePackage,
        state: assertPackageState(shouldPurge ? 'purged' : input.offlinePackage.state, 'QXRead OfflinePackage state'),
        updatedAt: purgedAt,
        grantsEntitlement: false,
        mutatesQxp: false,
        executesFlip: false,
    });
}
//# sourceMappingURL=advanced-offline.js.map